/**
 * Mock Server Control Utilities
 *
 * Provides utilities for controlling the mock server at runtime
 */

export interface MockServerStatus {
  isRunning: boolean;
  startedVia: 'url' | 'env' | 'manual' | null;
  startedAt: string | null;
}

class MockServerController {
  private status: MockServerStatus = {
    isRunning: false,
    startedVia: null,
    startedAt: null,
  };

  /**
   * Get current mock server status
   */
  getStatus(): MockServerStatus {
    return { ...this.status };
  }

  /**
   * Start mock server manually
   */
  async start(): Promise<void> {
    if (this.status.isRunning) {
      console.warn('🎭 Mock server is already running');
      return;
    }

    try {
      const { startMockServer } = await import('../mocks');
      await startMockServer();

      this.status = {
        isRunning: true,
        startedVia: 'manual',
        startedAt: new Date().toISOString(),
      };

      console.log('🎭 Mock server started manually');
      this.logTestAccounts();
    } catch (error) {
      console.error('❌ Failed to start mock server:', error);
      throw error;
    }
  }

  /**
   * Stop mock server
   */
  async stop(): Promise<void> {
    if (!this.status.isRunning) {
      console.warn('🛑 Mock server is not running');
      return;
    }

    try {
      const { worker } = await import('../mocks/browser');
      worker.stop();

      this.status = {
        isRunning: false,
        startedVia: null,
        startedAt: null,
      };

      console.log('🛑 Mock server stopped');
    } catch (error) {
      console.error('❌ Failed to stop mock server:', error);
      throw error;
    }
  }

  /**
   * Restart mock server
   */
  async restart(): Promise<void> {
    await this.stop();
    await this.start();
  }

  /**
   * Check if URL parameter indicates mock should be enabled
   */
  shouldEnableFromUrl(): boolean {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('mock') === 'true';
  }

  /**
   * Update URL to enable/disable mock parameter
   */
  updateUrlParameter(enable: boolean): void {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    if (enable) {
      url.searchParams.set('mock', 'true');
    } else {
      url.searchParams.delete('mock');
    }

    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Mark server as started (used by main.tsx)
   */
  markAsStarted(via: 'url' | 'env'): void {
    this.status = {
      isRunning: true,
      startedVia: via,
      startedAt: new Date().toISOString(),
    };
  }

  /**
   * Log available test accounts
   */
  private logTestAccounts(): void {
    console.log('📋 Available test accounts:');
    console.log('  Publisher: publisher@test.com / Test@123');
    console.log('  Publisher 2: publisher2@test.com / Test@123');
    console.log('  Reviewer: reviewer@alc.com / Reviewer@123');
    console.log('  Admin: admin@arabookverse.com / Admin@123');
  }

  /**
   * Log mock server endpoints
   */
  logEndpoints(): void {
    console.log('🔗 Mock Server Endpoints:');
    console.log('User Management:');
    console.log('  POST /v1/users/login');
    console.log('  POST /v1/users/register');
    console.log('  GET /v1/users/profile');
    console.log('  POST /v1/users/files/business-license');
    console.log('');
    console.log('Publisher Service:');
    console.log('  GET /v1/publishers/{publisherId}');
    console.log('  GET /v1/publishers/{publisherId}/books');
    console.log('  POST /v1/books/{bookId}/attachments');
    console.log('  POST /v1/publishers/{publisherId}/requests');
    console.log('');
    console.log('💡 Use ?mock=true in URL to enable automatically');
  }
}

// Create singleton instance
export const mockServerController = new MockServerController();

// Make it globally available in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).mockServer = {
    start: () => mockServerController.start(),
    stop: () => mockServerController.stop(),
    restart: () => mockServerController.restart(),
    status: () => mockServerController.getStatus(),
    endpoints: () => mockServerController.logEndpoints(),
    enableUrl: () => mockServerController.updateUrlParameter(true),
    disableUrl: () => mockServerController.updateUrlParameter(false),
  };

  // Log available console commands
  console.log('🛠️ Mock Server Console Commands Available:');
  console.log('  window.mockServer.start()     - Start mock server');
  console.log('  window.mockServer.stop()      - Stop mock server');
  console.log('  window.mockServer.restart()   - Restart mock server');
  console.log('  window.mockServer.status()    - Get current status');
  console.log('  window.mockServer.endpoints() - List available endpoints');
  console.log('  window.mockServer.enableUrl() - Add ?mock=true to URL');
  console.log('  window.mockServer.disableUrl() - Remove ?mock=true from URL');
}

export default mockServerController;
